  <template>
<div class="dashboard" >
  <div class="container" v-if="snapshotsMissing">
    <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
  </div>
  <div class="container" v-else-if="isDataFetched">
    <div class="dashboard__row">
      <base-content-block class="dashboard__row-item"
                          title="Comparison">
        <template v-if="isCompetitorsExist">
        <span slot="title-additional">
          <base-more-details-link to="/reports/rank-zone/comparison" />
        </span>
        <div class="dashboard__chart-wrapper">
          <PieChart class="charts__pie"
                    :data="competition.items"
                    hasHeader/>
        </div>
        </template>
        <overview-comparison-placeholder v-else />
      </base-content-block>
      <base-content-block class="dashboard__row-item"
                          title="Rank Zones">
        <base-more-details-link to="/reports/rank-zone/url"
                           slot="title-additional" />
        <div class="dashboard__chart-wrapper">
          <PieChart class="charts__pie"
                    :data="rankZones.items"
                    :footerItem="rankZones.footer"/>
        </div>
      </base-content-block>
    </div>
    <div class="dashboard__row">
      <base-content-block class="dashboard__row-item"
                          title="Sections">
        <base-more-details-link to="/reports/rank-zone/sections"
                           slot="title-additional" />
        <div class="dashboard__chart-wrapper">
          <PieChart class="charts__pie"
                    :data="section.items"
                    :footerItem="section.footer"/>
        </div>
      </base-content-block>
      <base-content-block class="dashboard__row-item"
                          title="Status">
        <base-more-details-link to="/reports/rank-zone/status"
                           slot="title-additional" />
        <div class="dashboard__chart-wrapper">
          <PieChart class="charts__pie"
                    :data="status.items"
                    :footerItem="status.footer"/>
        </div>
      </base-content-block>
    </div>
    <div class="dashboard__row">
      <base-content-block class="dashboard__row-item"
                          style="margin-right: 0;"
                          title="Relevancy Score Breakdown"
                          slotLess="true">
        <div v-if="isScoreAbsent"
             class="no-data-block">
          Awaiting Data
        </div>
        <div class="dashboard__chart-wrapper" v-else>
          <breakdown :data="breakdown"/>
        </div>
      </base-content-block>
    </div>
<!--    <base-content-block :headerStyle="{ 'border-bottom': 'none' }"-->
<!--                        title="Temporal Overall Ranking">-->
<!--      <base-table-collapsible class="dashboard__table"-->
<!--                              :config="config"-->
<!--                              :sorted="false"-->
<!--                              :headerStyle="{ 'background-color': 'transparent' }"-->
<!--                              :show-summary="false"-->
<!--                              :data="snapshotsData" />-->
<!--    </base-content-block>-->
  </div>
  <div class="container" v-else>
    <div class="rank-zone__loader">
    <base-loader />
    </div>
  </div>
</div>
</template>

<script>
import _ from 'lodash'
import PieChart from '@/features/reports/common/PieChart'
import { mapState, mapActions } from 'vuex'
import { baseComponent, topLevelTableConfig } from '@/features/reports/common/Mixins'
import { moveOtherToEnd, moveClientToStart } from '@/features/reports/common/Helper'
import OverviewComparisonPlaceholder from '@/features/reports/common/OverviewComparisonPlaceholder'
import Breakdown from './Breakdown'

/**
 * Overview component for RankZone
 */
export default {
  name: 'Overview',
  mixins: [baseComponent, topLevelTableConfig],
  created () {
    this.prepareDataProperties()
    if (this.selectedClient) {
      this.loadPageData(this.clientId)
    }
  },
  data () {
    return {
      // page specific table config
      topLevelConfig: {
        highlightProp: 'type',
        highlightValue: 'sibling',
        children: null
      }
    }
  },
  components: {
    OverviewComparisonPlaceholder,
    PieChart,
    Breakdown
  },
  computed: {
    ...mapState('rankZoneOverview', {
      /**
       * prepare PieChart data for Competition block
       * */
      competition: state => {
        let total = 0
        let competitionData = state.overviewData.competition.map(item => {
          total += item.total_msv
          return {
            name: item.label,
            intermediate: item.rank,
            value: item.total_msv,
            type: item.type
          }
        })
        competitionData = moveClientToStart(competitionData)
        competitionData = moveOtherToEnd(competitionData, ['value', 'intermediate'])
        return {
          footer: {
            name: 'Est. Monthly Traffic:',
            value: total
          },
          items: competitionData
        }
      },
      rankZones: function (state) {
        return this.formatPieChartData(state.overviewData.rank_zones)
      },
      section: function (state) {
        return this.formatPieChartData(state.overviewData.sections)
      },
      status: function (state) {
        return this.formatPieChartData(state.overviewData.statuses)
      },
      total (state) {
      },
      breakdown (state) {
        return {
          list: state.overviewData.breakdown.sort((item1, item2) => item2.score - item1.score),
          total: state.overviewData.keywords_total
        }
      },
      snapshotsData: state => (state.overviewData.rank_zone_snapshots || [])
    }),
    /**
     * there is only one level table - without child
     * */
    config () {
      const topLevelConfig = this.topLevelConfig
      return [
        this.topLevelConfig
      ]
    },
    isCompetitorsExist () {
      return this.competition && this.competition.items.some(item => item.type === 'Competitors')
    },
    isScoreAbsent () {
      return !this.breakdown || !this.breakdown.list || !this.breakdown.list.length
    }
  },
  watch: {
    selectedClient (client) {
      if (!client) {
        return
      }
      // reset page data if client has been changed
      this.isDataFetched = false
      this.resetOverviewData()
      this.loadPageData()
    }
  },
  methods: {
    ...mapActions('rankZoneOverview', [
      'getOverviewData',
      'resetOverviewData'
    ]),
    /**
     * modify default table config
     */
    prepareDataProperties: function () {
      const sortDatePreFormatter = (timestamp) => {
        if (!timestamp) {
          return null
        }
        return moment(timestamp).valueOf()
      }
      const labels = ['Rank Zone Snapshot', '01 - 03', '04 - 07', '08 - 10', '11 - 25', '26 - 50', '51 - 100', 'NR', 'ND', 'Total']
      this.topLevelConfig.cells = this.topLevelConfig.cells.reduce((result, item, index) => {
        if (item.propName !== 'keyword_total') {
          result.push({
            ...item,
            filterName: index === 0 ? 'formattedDate' : item.filterName,
            sortPreFormatting: index === 0 ? sortDatePreFormatter : null,
            label: labels[index]
          })
        }
        return result
      }, [])
      this.topLevelConfig.cells[0].style['flex-basis'] = '15%'
    },
    async loadPageData () {
      if (this.snapshotsMissing) {
        return
      }
      const data = await this.getOverviewData(this.clientId)
      data && (this.isDataFetched = true)
    },
    /**
     * Prepare PieChart data
     * @param source - corresponging store data
     * @returns {{footer: {name: string, value: *}, items: any | {name: string, value: *}[]}}
     */
    formatPieChartData (source) {
      const { total_msv, ...body } = source
      const keys = Object.keys(body)
      let items = keys.map(key => {
        return {
          name: key,
          value: body[key]
        }
      })

      items = _.orderBy(items, ['name'], ['asc'])
      items = moveOtherToEnd(items)
      return {
        items,
        footer: {
          name: 'Total MSV:',
          value: total_msv
        }
      }
    }
  }
}

</script>

<style scoped lang="scss">
.dashboard {
  margin-bottom: -20px;

  &__row {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    margin-bottom: 14px;

    &-item {
      flex: 1 1 100%;
      overflow: hidden;

      &:first-child {
        margin-right: 14px;
      }
    }
  }

  &__chart-wrapper {
    width: 100%;
    padding: 15px 0 30px 40px;
    box-sizing: border-box;
  }

  &__table {
    flex: 1 1;
  }
}
</style>
